import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Col, Radio, Row, Space, Typography } from 'antd';

import { StyledModal } from 'common/components';
import WarnIcon from 'common/components/dialog-function/WarnIcon';
import {
  checkFieldsChange,
  checkFieldsChangeBulkEdit,
  checkDetailFieldsChange,
  lowerCaseFieldName,
} from './utils';
import { checkIsValidObject } from 'utils/common/checkIsValidObject';

import { useGetProductFullView } from 'hooks';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import {
  ADVANCE_MODULE,
  BASE_MODULE,
  BASIC_FIELD,
  TDC_PROPERTIES,
  LABELING,
  NUTRITION,
  SUPPLEMENT,
  PET_NUTRITION,
} from './IXONEConstants';

import {
  SUPPLEMENT_TYPE,
  NUTRITION_TYPE,
  PET_NUTRITION_TYPE,
} from 'pages/product-full-view/components/product-media-area/multiple-panel/constants';
import { PRODUCT_IMAGERY_LABELS_BY_LEVEL } from './components/constants';

export const TYPE_VIEW = {
  PRODUCT_DETAIL: 'PRODUCT_DETAIL',
  EDIT_PRODUCT: 'EDIT_PRODUCT',
  FACT_PANEL: 'FACT_PANEL',
  DELETE_FACT_PANEL: 'DELETE_FACT_PANEL',
  INGREDIENTS: 'INGREDIENTS',
  ALLERGENS: 'ALLERGENS',
  IMAGE_PRODUCT: 'IMAGE_PRODUCT',
  BULK_EDIT: 'BULK_EDIT',
  IMAGE_ASSET: 'IMAGE_ASSET',
  IMPORT_PRODUCT_TEMPLATE: 'IMPORT_PRODUCT_TEMPLATE',
};

const DELETE_PANEL_DISPLAY_NAME = {
  ALLERGENS: 'Allergen Panel',
  INGREDIENTS: 'Ingredient Panel',
  NutritionFactsPanel: 'Nutrition Facts Panel',
  PetNutritionFactsPanel: 'Pet Nutrition Fact Panel',
  SupplementFactsPanel: 'Supplement Facts Panel',
};

const SUBTYPE_FIELD_MAP = {
  [NUTRITION_TYPE]: NUTRITION,
  [SUPPLEMENT_TYPE]: SUPPLEMENT,
  [PET_NUTRITION_TYPE]: PET_NUTRITION,
};

export const IXONECertificationWrapper = (props) => {
  const { id } = useParams();
  const {
    onConfirm,
    type,
    subType,
    onCancel,
    showModal,
    Component,
    loading,
    children,
  } = props;

  const [modalConfirm, setModalConfirm] = useState(false);
  const [fieldList, setFieldList] = useState([]);
  const [options, setOptions] = useState('cancel');

  const pathName = window.location.pathname;
  const productId = pathName?.includes('product') ? id : null;

  const { productFull } = useGetProductFullView({ productId });

  const { ixOneCertifiedStatus, fromTdcProduct, ixOneId } = productFull;

  const hasBypassPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const SUBTYPE_FIELD = SUBTYPE_FIELD_MAP[subType] || {};

  const checkShowConfirmModal = (information) => {
    if (hasBypassPermission) return false;

    const isValidObject = checkIsValidObject(information);

    if (isValidObject) return information?.condition;

    const hasIXONEShield =
      ixOneId && ixOneCertifiedStatus !== 'Removed' && fromTdcProduct;

    return productId ? hasIXONEShield && showModal : showModal;
  };

  const productImage =
    PRODUCT_IMAGERY_LABELS_BY_LEVEL[productFull?.packageLevel || 'Unit'];

  const productImageLowerCase = lowerCaseFieldName(productImage);

  const IMAGE_ASSET_TYPE = [TYPE_VIEW.IMAGE_ASSET];

  const IMAGE_TYPE = IMAGE_ASSET_TYPE.concat([TYPE_VIEW.IMAGE_PRODUCT]);

  const TDC_FIELD = {
    ...TDC_PROPERTIES,
    ...BASE_MODULE,
    ...ADVANCE_MODULE,
    ...BASIC_FIELD,
    ...LABELING,
    ...SUBTYPE_FIELD,
    ...productImageLowerCase,
  };
  const isDelete =
    type === TYPE_VIEW.ALLERGENS ||
    type === TYPE_VIEW.INGREDIENTS ||
    type === TYPE_VIEW.DELETE_FACT_PANEL;

  const handleConfirmIXONE = ({
    originalData,
    changedFields,
    otherInformation,
  }) => {
    let hasChangedField = false;

    // const showConfirmModal = true;
    const showConfirmModal = checkShowConfirmModal(otherInformation);

    if (!showConfirmModal) {
      onConfirm(null);
      return;
    }

    if (type === TYPE_VIEW.PRODUCT_DETAIL) {
      hasChangedField = checkDetailFieldsChange({
        originalData,
        changedFields,
      });
    }

    if (type === TYPE_VIEW.EDIT_PRODUCT) {
      hasChangedField = checkFieldsChange({
        originalData,
        changedFields,
      });
    }

    if (type === TYPE_VIEW.IMAGE_PRODUCT) {
      hasChangedField = {
        result: true,
        list: ['Image'],
      };
    }

    if (type === TYPE_VIEW.IMAGE_ASSET) {
      hasChangedField = {
        result: true,
        list: otherInformation?.list,
      };
    }

    if (isDelete) {
      //* DELETE
      hasChangedField = {
        result: true,
        list: [
          DELETE_PANEL_DISPLAY_NAME[type] || DELETE_PANEL_DISPLAY_NAME[subType],
        ],
      };
    }

    if (type === TYPE_VIEW.FACT_PANEL && changedFields) {
      hasChangedField = checkFieldsChange({
        originalData,
        changedFields,
        subType,
      });
    }

    if (type === TYPE_VIEW.BULK_EDIT) {
      hasChangedField = checkFieldsChangeBulkEdit(changedFields, originalData);
    }

    if (type === TYPE_VIEW.IMPORT_PRODUCT_TEMPLATE) {
      hasChangedField = { result: true, list: [] };
    }

    if (hasChangedField?.result) {
      setModalConfirm(hasChangedField?.result);
      setFieldList(hasChangedField?.list);
    } else onConfirm(options);
  };

  const handleCancel = () => {
    setOptions('cancel');
    setModalConfirm(false);
  };

  const handleOk = () => {
    setModalConfirm(false);

    if (options === 'continue' || options === 'ignore') {
      onConfirm && onConfirm(options, fieldList);
    }

    if (options === 'cancel') {
      onCancel && onCancel();
    }
  };

  const fieldListContent = fieldList?.length ? (
    <>
      <Col>
        <Typography.Paragraph>
          Below is a list of the IX-ONE captured fields being edited:
        </Typography.Paragraph>
      </Col>
      <Col span={24} style={{ fontWeight: 'bold' }}>
        <ol>
          {fieldList?.length
            ? fieldList.map((fieldName, index) => {
                if (typeof fieldName !== 'object') {
                  return (
                    <li key={index}>
                      <Typography.Text>
                        {TDC_FIELD?.[fieldName?.toLowerCase()] || fieldName}
                      </Typography.Text>
                    </li>
                  );
                } else return null;
              })
            : null}
        </ol>
      </Col>
    </>
  ) : null;

  return (
    <>
      <StyledModal
        zIndex={9999}
        title={null}
        width={490}
        bodyStyle={{ paddingTop: 24 }}
        visible={modalConfirm}
        masClosable={false}
        onCancel={handleCancel}
        footer={[
          <Row justify='end'>
            <Col>
              <Space>
                <Button
                  type='primary'
                  disabled={!options}
                  onClick={handleOk}
                  loading={loading}
                >
                  Yes
                </Button>
                {/* <Button onClick={handleCancel}>Cancel</Button> */}
              </Space>
            </Col>
          </Row>,
        ]}
      >
        <WarnIcon />
        <div
          style={{
            color: '#6c757d',
            fontSize: 16,
            textAlign: 'left',
            marginTop: 4,
          }}
        >
          <Row style={{ padding: '10px 0' }}>
            <Col>
              <Typography.Paragraph>
                You have edited a field or fields which were collected during an
                IX-ONE capture event. Many of our retailer and distributor
                partners will only accept IX-ONE Certified content. Removing
                this certification may cause one or more of your partners to
                reject your product information.
              </Typography.Paragraph>
            </Col>
            {Component ? <Component fieldList={fieldList} /> : fieldListContent}
            <Col>
              <Typography.Paragraph>
                To restore IX-ONE Certified status, a recapture of your product
                would be required. Are you sure you wish to proceed and remove
                your IX-ONE certification?
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <Radio.Group
                onChange={(e) => {
                  setOptions(e.target.value);
                }}
                value={options}
                style={{ width: '100%', textAlign: 'center' }}
              >
                <Radio value='continue'>Continue</Radio>
                {fieldList?.length &&
                !isDelete &&
                !IMAGE_TYPE.includes(type) ? (
                  <Radio value='ignore'>
                    Update non-IX-ONE captured fields only
                  </Radio>
                ) : null}
                <Radio value='cancel'>Cancel</Radio>
              </Radio.Group>
            </Col>
          </Row>
        </div>
      </StyledModal>

      {children(handleConfirmIXONE)}
    </>
  );
};

export const IXONEShieldWarning = ({
  text = 'product',
  ixoneIgnore,
  children,
}) => {
  return (
    <>
      {ixoneIgnore ? (
        <Typography.Text>
          The {text} will be deleted by choosing the&nbsp;
          <Typography.Text style={{ textTransform: 'capitalize' }} strong>
            {ixoneIgnore}
          </Typography.Text>
          &nbsp;option. Are you sure you want to proceed with the deletion?
        </Typography.Text>
      ) : (
        <Typography.Text>{children}</Typography.Text>
      )}
    </>
  );
};
